import React, { useEffect } from 'react';
import { Heart, Star, Check, Shield, Gift, BookOpen, Sparkles, Lock, Zap, Quote, Gem, Feather, Brain, Target, Eye, ShieldCheck, Moon, Sun, Flower, Crown } from 'lucide-react';
import { Button } from './ui/Button';
import { PurchaseCard } from './ui/PurchaseCard';
import { TopBanner } from './ui/TopBanner';
import { useCurrency } from '../hooks/useCurrency';
import { AudioPlayer } from './ui/AudioPlayer';
import { CountdownTimer } from './ui/CountdownTimer';
export function LandingPage() {
  const checkoutUrl = 'https://buy.stripe.com/fZu3cvfpHf868h42kfefC07';
  const goToCheckout = () => {
    window.open(checkoutUrl, '_blank');
  };
  const ebookImage = 'https://cdn.magicpatterns.com/uploads/4NVypCqEvgUTaWmQZyk98X/hf_20260212_223854_a84ce896-3aca-484e-bacf-2d3ee372b502.png';
  const audioUrl = 'https://cdn.magicpatterns.com/uploads/p8CheZvvjmsyi4B8cLdJ2W/Bienvenida a manifiesta tu pareja ideal.mp4';
  const {
    formatPrice
  } = useCurrency();
  // Scroll reveal effect
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal, .reveal-stagger');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#FAF7F5]" data-id="element-1">
      {/* Top Banner */}
      <TopBanner data-id="element-2" />

      {/* Floating Particles Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10" data-id="element-3">
        <div className="absolute top-20 left-10 w-2 h-2 bg-[#C9A86A]/30 rounded-full animate-float" style={{
        animationDelay: '0s'
      }} data-id="element-4" />
        <div className="absolute top-40 right-20 w-3 h-3 bg-[#D4A5A5]/20 rounded-full animate-float" style={{
        animationDelay: '1s'
      }} data-id="element-5" />
        <div className="absolute top-60 left-1/4 w-2 h-2 bg-[#C9A86A]/20 rounded-full animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-6" />
        <div className="absolute bottom-40 right-1/3 w-2 h-2 bg-[#E8D5D0]/40 rounded-full animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-7" />
      </div>

      {/* Navigation */}
      <nav className="sticky top-0 left-0 right-0 z-50 glass border-b border-[#C9A86A]/10 transition-all duration-300" data-id="element-8">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center" data-id="element-9">
          <div className="text-xl font-serif font-bold tracking-[0.2em] text-[#3A3A3A]" data-id="element-10">
            ALMA EN <span className="text-gradient-gold" data-id="element-11">EXPANSIÓN</span>
          </div>
          <Button onClick={goToCheckout} variant="outline" className="text-sm px-6 py-2" data-id="element-12">
            OBTENER AHORA
          </Button>
        </div>
      </nav>

      {/* 1. HERO SECTION */}
      <header className="relative min-h-screen flex items-center justify-center px-6 pt-20 pb-20 overflow-hidden" data-id="element-13">
        {/* Animated Background */}
        <div className="absolute inset-0 -z-10" data-id="element-14">
          <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-[#FAF7F5] to-[#FAF7F5]" data-id="element-15" />
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-br from-[#C9A86A]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '4s'
        }} data-id="element-16" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D4A5A5]/20 to-transparent rounded-full blur-[100px] animate-pulse" style={{
          animationDuration: '5s'
        }} data-id="element-17" />
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-18">
          {/* Left: Text Content */}
          <div className="text-center lg:text-left space-y-8 animate-fade-in-up" data-id="element-19">
            <div className="inline-block px-4 py-1.5 rounded-full bg-[#C9A86A]/10 text-[#C9A86A] text-xs font-bold tracking-widest uppercase mb-2 border border-[#C9A86A]/20" data-id="element-20">
              Programa de 21 días
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#3A3A3A] leading-[1.1] tracking-tight" data-id="element-21">
              Yo Soy la Mujer que Atrae
            </h1>

            <p className="text-xl md:text-2xl text-[#C9A86A] font-serif italic" data-id="element-22">
              Activa tu Energía Femenina y Manifiesta la Vida que Anhelas
            </p>

            {/* New Program Details */}
            <div className="flex flex-col gap-2 text-gray-600 font-medium text-sm md:text-base" data-id="element-23">
              <div className="flex items-center gap-2 justify-center lg:justify-start" data-id="element-24">
                <Check className="w-4 h-4 text-[#C9A86A]" data-id="element-25" />
                <span data-id="element-26">Programa digital guiado de 21 días</span>
              </div>
              <div className="flex items-center gap-2 justify-center lg:justify-start" data-id="element-27">
                <Check className="w-4 h-4 text-[#C9A86A]" data-id="element-28" />
                <span data-id="element-29">Incluye ebook + audios + ejercicios diarios</span>
              </div>
              <div className="flex items-center gap-2 justify-center lg:justify-start" data-id="element-30">
                <Check className="w-4 h-4 text-[#C9A86A]" data-id="element-31" />
                <span data-id="element-32">Solo necesitas 15 minutos al día</span>
              </div>
              <div className="flex items-center gap-2 justify-center lg:justify-start" data-id="element-33">
                <Check className="w-4 h-4 text-[#C9A86A]" data-id="element-34" />
                <span data-id="element-35">Acceso inmediato desde cualquier país</span>
              </div>
            </div>

            <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-36">
              <p data-id="element-37">
                No es casualidad que estés aquí. Si has llegado hasta este
                programa es porque en tu interior ya sabes que estás lista para
                algo más:
              </p>
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start font-medium text-[#3A3A3A]" data-id="element-38">
                {['Más amor', 'Más abundancia y Dinero', 'Más seguridad', 'Más claridad', 'Más poder'].map((item, i) => <span key={i} className="bg-white px-3 py-1 rounded-full shadow-sm border border-[#E8D5D0]" data-id="element-39">
                    {item}
                  </span>)}
              </div>
              <p className="font-serif text-xl text-[#3A3A3A] font-medium pt-2" data-id="element-40">
                Más eso de "Atraer lo que realmente deseas"
              </p>
            </div>

            <div className="w-full max-w-md mx-auto lg:mx-0 bg-white/50 rounded-2xl p-4 border border-[#E8D5D0]" data-id="element-41">
              <AudioPlayer src={audioUrl} data-id="element-42" />
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start" data-id="element-43">
              <Button onClick={goToCheckout} variant="luxury" withSparkle className="text-lg px-10 py-5 w-full sm:w-auto shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all" data-id="element-44">
                OBTENER AHORA
              </Button>
              <div className="flex flex-col items-start" data-id="element-45">
                <span className="text-xs text-gray-500 uppercase tracking-wider font-bold" data-id="element-46">
                  Oferta Especial
                </span>
                <span className="text-xl font-serif font-bold text-[#3A3A3A]" data-id="element-47">
                  {formatPrice(19.99)}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Floating Ebook */}
          <div className="relative flex justify-center animate-scale-in" style={{
          animationDelay: '0.3s'
        }} data-id="element-48">
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="relative animate-float-subtle block group perspective-1000" data-id="element-49">
              <img src={ebookImage} alt="Manifiesta tu Pareja Ideal" className="w-full max-w-[450px] h-auto drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)] cursor-pointer hover:scale-105 hover:rotate-1 transition-all duration-700 ease-out" data-id="element-50" />
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#C9A86A]/20 to-transparent rounded-3xl blur-3xl -z-10 scale-110" data-id="element-51" />
              {/* Bestseller Badge */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white px-5 py-2 rounded-full text-xs font-bold shadow-lg animate-pulse tracking-widest uppercase" data-id="element-52">
                ⭐ Bestseller
              </div>
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#C9A86A]/60 animate-bounce" data-id="element-53">
          <span className="text-xs tracking-widest uppercase" data-id="element-54">
            Descubre Más
          </span>
          <div className="w-6 h-10 rounded-full border-2 border-current flex justify-center pt-2" data-id="element-55">
            <div className="w-1 h-2 bg-current rounded-full animate-pulse" data-id="element-56" />
          </div>
        </div>
      </header>

      {/* 2. PROBLEM/AWAKENING */}
      <section className="py-20 px-6 bg-white relative overflow-hidden" data-id="element-57">
        <div className="max-w-4xl mx-auto text-center" data-id="element-58">
          <div className="reveal space-y-8" data-id="element-59">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-[#3A3A3A] leading-tight" data-id="element-60">
              Durante años nos enseñaron a competir, a perseguir, a sobrevivir.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto italic font-serif" data-id="element-61">
              Pero nadie nos enseñó a CREAR desde nuestra energía femenina.
            </p>
          </div>

          <div className="mt-16 reveal" data-id="element-62">
            <p className="text-lg text-gray-800 mb-8 font-medium" data-id="element-63">
              Cuando una mujer activa su energía femenina consciente, como te
              enseñará este programa, su realidad cambia y atraes:
            </p>

            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-id="element-64">
              {[{
              text: 'Relaciones poderosas',
              icon: Heart
            }, {
              text: 'Negocios exitosos',
              icon: Briefcase
            }, {
              text: 'Hábitos atómicos',
              icon: Zap
            }, {
              text: 'Desde mi energía femenina',
              icon: Sparkles
            }].map((item, i) => <div key={i} className="flex items-center gap-4 p-5 rounded-2xl bg-[#FAF7F5] border border-[#E8D5D0] hover:border-[#C9A86A] transition-colors duration-300" data-id="element-65">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-[#C9A86A]" data-id="element-66">
                    <item.icon className="w-5 h-5" data-id="element-67" />
                  </div>
                  <span className="text-lg font-serif text-[#3A3A3A]" data-id="element-68">
                    {item.text}
                  </span>
                </div>)}
            </div>

            <div className="mt-12" data-id="element-69">
              <Button onClick={goToCheckout} variant="primary" withIcon data-id="element-70">
                QUIERO ACTIVAR MI ENERGÍA
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 3. EN 21 DÍAS COMENZARÁS A... */}
      <section className="py-20 px-6 bg-[#FAF7F5]" data-id="element-71">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center" data-id="element-72">
          <div className="reveal" data-id="element-73">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase block mb-4" data-id="element-74">
              Transformación Total
            </span>
            <h2 className="text-4xl md:text-5xl font-serif text-[#3A3A3A] mb-8" data-id="element-75">
              En 21 días comenzarás a:
            </h2>

            <ul className="space-y-6" data-id="element-76">
              {['Reconectar con tu poder interno', 'Elevar tu magnetismo natural', 'Atraer relaciones más conscientes', 'Crear oportunidades sin perseguirlas', 'Tomar decisiones desde tu valor, no desde el miedo'].map((item, i) => <li key={i} className="flex items-start gap-4 group" data-id="element-77">
                  <div className="w-6 h-6 rounded-full bg-[#C9A86A] flex items-center justify-center flex-shrink-0 mt-1 group-hover:scale-110 transition-transform" data-id="element-78">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-79" />
                  </div>
                  <span className="text-lg text-gray-700 group-hover:text-[#3A3A3A] transition-colors" data-id="element-80">
                    {item}
                  </span>
                </li>)}
            </ul>
            <p className="mt-6 text-[#C9A86A] font-serif italic text-xl pl-10" data-id="element-81">
              Te aseguro que CREARÁS tu realidad.
            </p>
          </div>

          <div className="reveal bg-white p-8 md:p-10 rounded-[2rem] shadow-xl border border-[#E8D5D0]/50 relative overflow-hidden" data-id="element-82">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A86A]/10 rounded-bl-full" data-id="element-83" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#D4A5A5]/10 rounded-tr-full" data-id="element-84" />

            <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-6" data-id="element-85">
              Reprogramación Emocional y Energética
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed" data-id="element-86">
              <p data-id="element-87">
                Es un proceso profundo. Atraemos a nuestro amor ideal, esa
                pareja con la que tanto sueñas, a soltar lo que ya no va
                contigo.
              </p>
              <p data-id="element-88">
                Aprenderás a generar más acción y productividad, más ingresos, a
                sentirte más viva.
              </p>
              <p className="font-medium text-[#3A3A3A]" data-id="element-89">
                En definitiva, eliminar viejos patrones para dar la bienvenida a
                lo nuevo.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. PROGRAM CONTENTS */}
      <section className="py-20 px-6 bg-white" data-id="element-90">
        <div className="max-w-6xl mx-auto" data-id="element-91">
          <div className="text-center mb-16 reveal" data-id="element-92">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase" data-id="element-93">
              ¿Qué vas a encontrar dentro?
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-4 mb-4" data-id="element-94">
              Este programa está estructurado en 21 días de activación
              progresiva
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 reveal-stagger" data-id="element-95">
            {[{
            icon: Flower,
            text: 'Ejercicios diarios de reconexión femenina'
          }, {
            icon: Sparkles,
            text: 'Ritual de magnetismo y energía creadora'
          }, {
            icon: Star,
            text: 'Técnicas de manifestación alineadas con tu esencia'
          }, {
            icon: Moon,
            text: 'Trabajo de sombra emocional'
          }, {
            icon: Brain,
            text: 'Reprogramación subconsciente'
          }, {
            icon: Crown,
            text: 'Activación de autoestima magnética'
          }, {
            icon: Target,
            text: 'Plan práctico para relaciones, negocios y hábitos'
          }].map((item, index) => <div key={index} className="group relative bg-[#FAF7F5] p-8 rounded-2xl border border-[#E8D5D0]/50 hover:border-[#C9A86A] transition-all duration-500 hover:shadow-lg hover:-translate-y-1" data-id="element-96">
                <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-6 text-[#C9A86A] shadow-sm group-hover:scale-110 transition-transform duration-500" data-id="element-97">
                  <item.icon className="w-6 h-6" strokeWidth={1.5} data-id="element-98" />
                </div>
                <p className="text-[#3A3A3A] font-medium text-lg leading-relaxed" data-id="element-99">
                  {item.text}
                </p>
              </div>)}
          </div>

          <div className="bg-[#3A3A3A] rounded-[2rem] p-8 md:p-12 text-white reveal relative overflow-hidden" data-id="element-100">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#C9A86A] rounded-full blur-[100px] opacity-20" data-id="element-101" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#D4A5A5] rounded-full blur-[100px] opacity-20" data-id="element-102" />

            <div className="relative z-10 grid md:grid-cols-2 gap-12 items-center" data-id="element-103">
              <div data-id="element-104">
                <h3 className="text-3xl font-serif font-bold mb-6 text-[#C9A86A]" data-id="element-105">
                  Además incluye:
                </h3>
                <ul className="space-y-4" data-id="element-106">
                  {['Ebook de Salud, Dinero y Amor', 'Audios para visualizaciones y Meditación', 'Diario de Gratitud', 'Diario de la Palabra De Dios para nosotras', 'Ideas creativas para emprender y generar ingresos', 'Y más…'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-107">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#C9A86A]" data-id="element-108" />
                      <span className="text-lg text-gray-200" data-id="element-109">{item}</span>
                    </li>)}
                </ul>
              </div>
              <div className="text-center" data-id="element-110">
                <p className="text-xl font-serif italic mb-8 text-gray-300" data-id="element-111">
                  "Todo lo que necesitas para tu transformación en un solo
                  lugar"
                </p>
                <Button onClick={goToCheckout} variant="luxury" withSparkle className="w-full md:w-auto" data-id="element-112">
                  ACCEDER AL CONTENIDO
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. IMAGINA ESTO (Cinematic) */}
      <section className="py-24 px-6 bg-[#1a1a1a] text-white relative overflow-hidden" data-id="element-113">
        <div className="absolute inset-0 opacity-20" data-id="element-114">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} data-id="element-115" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-116">
          <span className="text-[#C9A86A] font-bold tracking-[0.3em] text-sm uppercase mb-6 block reveal" data-id="element-117">
            Visualización
          </span>
          <h2 className="text-4xl md:text-6xl font-serif mb-16 reveal" data-id="element-118">
            Imagina esto…
          </h2>

          <div className="grid gap-8 mb-16 reveal-stagger" data-id="element-119">
            {['Despertarte sintiendo paz', 'Hablar desde tu verdad sin miedo', 'Atraer personas que te respetan', 'Crear dinero sin agotarte', 'Sentirte femenina, segura y poderosa'].map((item, i) => <div key={i} className="text-2xl md:text-3xl font-light text-gray-300 hover:text-white transition-colors duration-300" data-id="element-120">
                {item}
              </div>)}
          </div>

          <p className="text-xl md:text-2xl text-[#C9A86A] font-serif italic reveal" data-id="element-121">
            Eso es lo que ocurre cuando activas tu energía femenina consciente.
          </p>
        </div>
      </section>

      {/* 6. SALES/OFFER SECTION */}
      <section id="sales-section" className="py-20 px-6 bg-gradient-to-b from-white to-[#FAF7F5]" data-id="element-122">
        <div className="max-w-6xl mx-auto" data-id="element-123">
          <div className="text-center mb-12 reveal" data-id="element-124">
            <div className="inline-block px-6 py-2 rounded-full bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5] text-white text-sm font-bold uppercase tracking-widest shadow-lg animate-pulse mb-8" data-id="element-125">
              🌟 Oferta especial por tiempo limitado
            </div>
            <CountdownTimer data-id="element-126" />
          </div>

          <div className="reveal grid lg:grid-cols-2 gap-12 items-center mt-12" data-id="element-127">
            {/* Floating Ebook */}
            <div className="flex flex-col items-center space-y-8" data-id="element-128">
              <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="relative animate-float-subtle block perspective-1000" data-id="element-129">
                <img src={ebookImage} alt="Ebook" className="w-full max-w-[420px] drop-shadow-[0_50px_100px_rgba(201,168,106,0.4)] cursor-pointer hover:scale-105 hover:rotate-1 transition-all duration-700" data-id="element-130" />
              </a>
            </div>

            {/* Purchase Card */}
            <div className="w-full" data-id="element-131">
              <PurchaseCard title="YO SOY una mujer que atrae" subtitle="Comienza a Manifestar la Vida que Mereces" featured={true} className="h-full transform hover:scale-[1.02] transition-transform duration-500" data-id="element-132" />
            </div>
          </div>
        </div>
      </section>

      {/* 7. GUARANTEE */}
      <section className="py-12 px-6 bg-[#FAF7F5]" data-id="element-133">
        <div className="max-w-3xl mx-auto reveal" data-id="element-134">
          <div className="bg-[#FFFBF5] border border-[#C9A86A]/30 rounded-2xl p-8 text-center shadow-lg relative overflow-hidden group hover:shadow-xl transition-all duration-500" data-id="element-135">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C9A86A] to-[#D4A5A5]" data-id="element-136" />
            <div className="w-16 h-16 bg-[#C9A86A]/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-500" data-id="element-137">
              <ShieldCheck className="w-8 h-8 text-[#C9A86A]" data-id="element-138" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-3" data-id="element-139">
              Garantía de 7 días
            </h3>
            <p className="text-gray-600 leading-relaxed" data-id="element-140">
              Si el programa no resuena contigo, puedes solicitar reembolso
              completo. Sin preguntas.
            </p>
          </div>
        </div>
      </section>

      {/* 8. BONUSES */}
      <section className="py-20 px-6 bg-white" data-id="element-141">
        <div className="max-w-6xl mx-auto" data-id="element-142">
          <div className="text-center mb-16 reveal" data-id="element-143">
            <span className="text-[#C9A86A] font-bold tracking-[0.2em] text-sm uppercase" data-id="element-144">
              Regalos Exclusivos
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-4" data-id="element-145">
              Bonos gratis al comprar (solo por hoy)
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 reveal-stagger" data-id="element-146">
            {[{
            title: 'BONUS 1',
            desc: 'Audio de reprogramación subconsciente mientras duermes',
            icon: '🌙'
          }, {
            title: 'BONUS 2',
            desc: 'Meditación guiada de activación femenina (15 minutos)',
            icon: '🧘‍♀️'
          }, {
            title: 'BONUS 3',
            desc: 'Diario de manifestación imprimible',
            icon: '📔'
          }, {
            title: 'BONUS 4',
            desc: 'Ritual de luna para manifestar relaciones conscientes',
            icon: '🌑'
          }, {
            title: 'BONUS 5',
            desc: 'Checklist de hábitos femeninos magnéticos',
            icon: '✨'
          }].map((bonus, index) => <div key={index} className="relative border border-[#E8D5D0] rounded-2xl p-8 hover:border-[#C9A86A] transition-all duration-500 bg-gradient-to-br from-[#FAF7F5] to-white hover:shadow-lg group" data-id="element-147">
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform duration-300" data-id="element-148">
                  {bonus.icon}
                </div>
                <h3 className="text-sm font-bold text-[#C9A86A] tracking-widest mb-2" data-id="element-149">
                  {bonus.title}
                </h3>
                <p className="text-lg text-[#3A3A3A] font-medium leading-snug" data-id="element-150">
                  {bonus.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. TESTIMONIALS */}
      <section className="py-20 px-6 bg-[#FAF7F5]" data-id="element-151">
        <div className="max-w-6xl mx-auto" data-id="element-152">
          <div className="text-center mb-16 reveal" data-id="element-153">
            <span className="text-[#C9A86A] font-semibold tracking-[0.2em] text-sm uppercase" data-id="element-154">
              Historias Reales
            </span>
            <h2 className="text-3xl md:text-4xl font-serif text-[#3A3A3A] mt-4" data-id="element-155">
              Mujeres que decidieron elegirse
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 reveal-stagger" data-id="element-156">
            {[{
            quote: 'Este programa me abrió los ojos. Entendí por qué siempre atraía el mismo tipo de hombre. Ahora estoy en una relación sana.',
            name: 'Carolina M.',
            location: 'Ciudad de México',
            highlight: 'Relación sana'
          }, {
            quote: 'Lloré con los primeros ejercicios porque me sentí identificada. Cambió mi forma de verme completamente.',
            name: 'Valentina R.',
            location: 'Buenos Aires',
            highlight: 'Transformación'
          }, {
            quote: 'Después de relaciones tóxicas, esto me mostró que no estoy rota, solo necesitaba sanar mi energía.',
            name: 'Mariana L.',
            location: 'Bogotá',
            highlight: 'Sanación'
          }, {
            quote: 'Es práctico. No es solo teoría, son ejercicios que puedes aplicar. En 21 días vi cambios reales.',
            name: 'Sofía G.',
            location: 'Lima',
            highlight: '21 días'
          }, {
            quote: 'Mi terapeuta me lo recomendó. Explica la energía femenina de forma clara y aplicable.',
            name: 'Andrea P.',
            location: 'Santiago',
            highlight: 'Recomendado'
          }, {
            quote: 'Tenía miedo de estar sola. Ahora disfruto mi soledad y sé que el amor correcto llegará.',
            name: 'Daniela F.',
            location: 'Medellín',
            highlight: 'Amor propio'
          }].map((testimonial, index) => <div key={index} className="bg-white p-8 rounded-2xl border border-[#E8D5D0]/50 shadow-sm hover:shadow-lg transition-all duration-500 card-luxury relative" data-id="element-157">
                <Quote className="w-8 h-8 text-[#E8D5D0] absolute top-6 right-6" data-id="element-158" />
                <span className="inline-block px-3 py-1 bg-[#C9A86A]/10 text-[#C9A86A] text-xs font-semibold rounded-full mb-4" data-id="element-159">
                  {testimonial.highlight}
                </span>
                <p className="text-gray-700 text-base leading-relaxed mb-6 italic" data-id="element-160">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3 border-t border-[#E8D5D0]/30 pt-4" data-id="element-161">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C9A86A] to-[#D4A5A5] flex items-center justify-center text-white font-serif font-bold text-sm" data-id="element-162">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div data-id="element-163">
                    <p className="font-semibold text-[#3A3A3A] text-sm" data-id="element-164">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-165">
                      {testimonial.location}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. AUTHOR SECTION */}
      <section className="py-16 px-6 bg-white border-y border-[#C9A86A]/10" data-id="element-166">
        <div className="max-w-3xl mx-auto text-center reveal" data-id="element-167">
          <div className="w-16 h-1 bg-[#C9A86A] mx-auto mb-6 rounded-full" data-id="element-168" />
          <h3 className="text-2xl font-serif font-bold text-[#3A3A3A] mb-2" data-id="element-169">
            Creado por Danielle E. S.
          </h3>
          <p className="text-gray-600 italic text-lg" data-id="element-170">
            12 años acompañando procesos de crecimiento personal.
          </p>
          <div className="w-16 h-1 bg-[#C9A86A] mx-auto mt-6 rounded-full" data-id="element-171" />
        </div>
      </section>

      {/* 11. FINAL CTA / EMOTIONAL CLOSE */}
      <section className="py-24 px-6 bg-gradient-to-b from-[#FAF7F5] to-white text-center relative overflow-hidden" data-id="element-172">
        <div className="absolute inset-0 opacity-30" data-id="element-173">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C9A86A] rounded-full blur-[150px]" data-id="element-174" />
        </div>

        <div className="max-w-4xl mx-auto relative z-10 reveal" data-id="element-175">
          <h2 className="text-3xl md:text-5xl font-serif text-[#3A3A3A] leading-tight mb-8" data-id="element-176">
            Si sigues haciendo lo mismo,
            <br data-id="element-177" />
            <span className="italic text-gray-500" data-id="element-178">
              seguirás obteniendo lo mismo.
            </span>
          </h2>

          <p className="text-xl md:text-2xl text-[#C9A86A] font-serif italic mb-12" data-id="element-179">
            Pero si decides activarte… tu realidad cambia.
          </p>

          <div className="bg-white/80 backdrop-blur-md p-8 md:p-12 rounded-[2rem] shadow-2xl border border-[#E8D5D0] max-w-2xl mx-auto" data-id="element-180">
            <p className="text-lg text-gray-800 font-medium mb-8" data-id="element-181">
              "La energía femenina no se persigue. Se despierta. Y cuando
              despierta, crea."
            </p>

            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" className="block w-48 mx-auto mb-8 hover:scale-105 transition-transform duration-500" data-id="element-182">
              <img src={ebookImage} alt="Ebook" className="w-full drop-shadow-2xl rounded-lg" data-id="element-183" />
            </a>

            <Button onClick={goToCheckout} variant="luxury" withSparkle className="text-xl px-12 py-6 w-full md:w-auto shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all" data-id="element-184">
              ACCESO INMEDIATO
            </Button>

            <div className="mt-6 flex flex-col items-center gap-2" data-id="element-185">
              <span className="text-gray-500 text-sm" data-id="element-186">
                Precio regular:{' '}
                <span className="line-through" data-id="element-187">{formatPrice(39.99)}</span>{' '}
                <span className="text-[#C9A86A] font-bold text-lg ml-2" data-id="element-188">
                  Hoy: {formatPrice(19.99)}
                </span>
              </span>
              <span className="text-xs text-gray-400 font-medium tracking-wide uppercase" data-id="element-189">
                Disponible desde cualquier país
              </span>
            </div>
          </div>

          <div className="pt-16 mt-16 border-t border-[#E8D5D0]" data-id="element-190">
            <p className="text-sm text-gray-400" data-id="element-191">
              © 2024 Alma en Expansión. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </section>
    </div>;
}
// Helper component for icons used in the grid
function Briefcase(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-192">
      <rect width="20" height="14" x="2" y="7" rx="2" ry="2" data-id="element-193" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" data-id="element-194" />
    </svg>;
}